import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { IMG, CONTACT } from "../data/content";
import { track } from "../lib/api";

const Line = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[620px] overflow-hidden grain" data-testid="hero-section">
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={IMG.hero}
          alt="Grand concert stage bathed in golden light at a Mega Celebrity Events production"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#070708]/70 via-[#070708]/35 to-[#070708]" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#070708]/60 via-transparent to-[#3A0C19]/30" />

      {/* gold frame */}
      <div className="absolute inset-4 sm:inset-8 border border-[#C9A55C]/25 pointer-events-none" />

      <motion.div style={{ opacity: fade }} className="relative z-10 h-full flex flex-col justify-end max-w-7xl mx-auto px-8 sm:px-16 pb-28 sm:pb-32">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-[0.65rem] sm:text-xs uppercase tracking-[0.45em] text-[#C9A55C] mb-6"
          data-testid="hero-eyebrow"
        >
          Mega Celebrity Events · AP & Telangana
        </motion.p>

        <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight text-[#F3EEE5] max-w-4xl" data-testid="hero-heading">
          <Line delay={0.35}>Where Every Entrance</Line>
          <Line delay={0.5}>
            Becomes a <em className="font-accent italic text-[#C9A55C]">Moment.</em>
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-6 max-w-xl text-sm sm:text-base text-[#A9ABB2] leading-relaxed"
          data-testid="hero-subcopy"
        >
          Celebrity appearances, luxury celebrations, live entertainment and high-impact productions — curated across Andhra Pradesh and Telangana.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1 }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => { track("create_event_click", { location: "hero" }); navigate("/plan-your-event"); }}
            className="btn-gold"
            data-testid="hero-create-event-btn"
          >
            Create Your Event
          </button>
          <button
            onClick={() => navigate("/portfolio")}
            className="btn-ghost"
            data-testid="hero-explore-work-btn"
          >
            Explore Our Work
          </button>
          <a
            href={`tel:${CONTACT.phonePrimaryRaw}`}
            onClick={() => track("call_click", { location: "hero" })}
            className="text-xs uppercase tracking-[0.2em] text-[#F3EEE5]/70 hover:text-[#C9A55C] transition-colors duration-300 underline underline-offset-8 decoration-[#C9A55C]/40"
            data-testid="hero-call-link"
          >
            Call {CONTACT.phonePrimary}
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        data-testid="scroll-indicator"
      >
        <span className="text-[0.55rem] uppercase tracking-[0.4em] text-[#A9ABB2]">Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="w-px h-10 bg-gradient-to-b from-[#C9A55C] to-transparent"
        />
      </motion.div>
    </section>
  );
};
